/**
 * Analytics + behaviour intelligence layer.
 *
 * Loads GA4 / Microsoft Clarity / Meta Pixel / TikTok Pixel from environment
 * variables. Each loader is a NO-OP when its respective ID is unset, so the
 * production deploy can have the hooks live with zero IDs and we just paste
 * IDs later via Emergent's env panel without any code change.
 *
 * The graph-navigation event taxonomy is the strategic asset:
 *   tcp_topic_view, tcp_entity_view, tcp_timeline_view, tcp_source_view,
 *   tcp_bookmark, tcp_citation_jump, tcp_citation_to_vault,
 *   tcp_rabbit_hole_step (cross-topic / entity / source navigation),
 *   tcp_search.
 *
 * All events flow to ALL configured destinations through a single track()
 * function so we never duplicate logic.
 */

const GA_ID = process.env.REACT_APP_GA_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const TIKTOK_PIXEL_ID = process.env.REACT_APP_TIKTOK_PIXEL_ID;

let loaded = false;
let lastPath = null;
let lastTopicSlug = null;
let lastEntitySlug = null;

function injectScript(src, attrs = {}) {
    return new Promise((resolve) => {
        const s = document.createElement("script");
        s.async = true;
        s.src = src;
        Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
        s.onload = resolve;
        s.onerror = resolve;
        document.head.appendChild(s);
    });
}

export function initAnalytics() {
    if (loaded) return;
    loaded = true;

    if (GA_ID) {
        // GA4 base tag is now installed inline in /public/index.html <head>
        // (per Google's recommended placement). We only need to ensure the
        // gtag function exists on window for our manual page_view + event
        // dispatch elsewhere in this module — the inline tag already sets
        // window.dataLayer + window.gtag, but we initialise defensively for
        // any path where the inline tag was blocked (ad-block, CSP).
        window.dataLayer = window.dataLayer || [];
        if (typeof window.gtag !== "function") {
            function gtag() { window.dataLayer.push(arguments); }
            window.gtag = gtag;
        }
        // Reconfigure with the SPA-friendly flags. Idempotent — safe to call
        // even if the inline tag already configured the same ID.
        window.gtag("config", GA_ID, { send_page_view: false, anonymize_ip: true });
        // No script injection here — the inline <script> in index.html owns it.
    }

    if (CLARITY_ID) {
        // Microsoft Clarity (session recording + heatmaps — free)
        (function (c, l, a, r, i, t, y) {
            c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
            t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
            y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
        })(window, document, "clarity", "script", CLARITY_ID);
    }

    if (META_PIXEL_ID) {
        // Meta Pixel
        !(function (f, b, e, v, n, t, s) {
            if (f.fbq) return; n = f.fbq = function () {
                n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
            }; if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0";
            n.queue = []; t = b.createElement(e); t.async = !0;
            t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
        })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
        window.fbq("init", META_PIXEL_ID);
    }

    if (TIKTOK_PIXEL_ID) {
        // TikTok Pixel
        !(function (w, d, t) {
            w.TiktokAnalyticsObject = t; const ttq = (w[t] = w[t] || []);
            ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
            ttq.setAndDefer = function (e, n) { e[n] = function () { e.push([n].concat(Array.prototype.slice.call(arguments, 0))); }; };
            for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
            ttq.instance = function (e) { const n = ttq._i[e] || []; for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(n, ttq.methods[i]); return n; };
            ttq.load = function (e) {
                const n = "https://analytics.tiktok.com/i18n/pixel/events.js"; ttq._i = ttq._i || {};
                ttq._i[e] = []; ttq._i[e]._u = n; ttq._t = ttq._t || {}; ttq._t[e] = +new Date();
                ttq._o = ttq._o || {}; ttq._o[e] = {};
                const o = document.createElement("script"); o.type = "text/javascript"; o.async = !0; o.src = n + "?sdkid=" + e + "&lib=" + t;
                const a = document.getElementsByTagName("script")[0]; a.parentNode.insertBefore(o, a);
            };
            ttq.load(TIKTOK_PIXEL_ID); ttq.page();
        })(window, document, "ttq");
    }
}

/**
 * Mapping from public client-side event names → bounded backend kinds.
 * Only these kinds will be persisted into Mongo `internal_events`.
 */
const INTERNAL_EVENT_MAP = {
    tcp_topic_view: "topic_view",
    tcp_entity_view: "entity_view",
    tcp_source_view: "source_reader_open",
    tcp_gate_view: "gate_view",
    tcp_gate_cta_click: "gate_cta_click",
    tcp_bookmark: "bookmark",
    tcp_share: "share",
    // Build 3 additions — behavioural intelligence
    tcp_pathway_click: "pathway_click",
    tcp_scroll_depth_25: "scroll_depth_25",
    tcp_scroll_depth_50: "scroll_depth_50",
    tcp_scroll_depth_75: "scroll_depth_75",
    tcp_scroll_depth_90: "scroll_depth_90",
    tcp_onboarding_response: "onboarding_response",
    tcp_gated_section_view: "gated_section_view",
};

// One stable anonymous browser id. Used so anonymous behaviour can be
// stitched into sessions without identifying the user. Generated once and
// kept across visits; cleared if the user clears storage (acceptable).
const ANON_KEY = "tcp:anon_session";
function _getAnonSession() {
    try {
        let id = localStorage.getItem(ANON_KEY);
        if (!id) {
            // Cheap UUID-ish — doesn't need crypto-grade uniqueness.
            id = "a-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36);
            localStorage.setItem(ANON_KEY, id);
        }
        return id;
    } catch {
        return null;
    }
}

function _emitInternal(eventName, props) {
    try {
        const kind = INTERNAL_EVENT_MAP[eventName];
        if (!kind || typeof window === "undefined") return;
        const ref = props?.slug || props?.key || props?.id || null;
        // Fire-and-forget — no auth needed, never block.
        const backend = process.env.REACT_APP_BACKEND_URL;
        if (!backend) return;
        const headers = { "Content-Type": "application/json" };
        const anon = _getAnonSession();
        if (anon) headers["X-Anon-Session"] = anon;
        fetch(`${backend}/api/events/track`, {
            method: "POST",
            headers,
            credentials: "include",
            keepalive: true,
            body: JSON.stringify({ kind, ref, meta: props || {} }),
        }).catch(() => {});
    } catch (_) { /* swallow */ }
}

/** Single track() function fanning out to every configured destination. */
export function track(eventName, props = {}) {
    if (typeof window === "undefined") return;
    try {
        if (window.gtag && GA_ID) window.gtag("event", eventName, props);
        if (window.clarity && CLARITY_ID) window.clarity("event", eventName);
        if (window.fbq && META_PIXEL_ID) window.fbq("trackCustom", eventName, props);
        if (window.ttq && TIKTOK_PIXEL_ID) window.ttq.track(eventName, props);
        // Always also emit a window event so we can locally observe in dev/tests
        window.dispatchEvent(new CustomEvent("tcp:analytics", { detail: { event: eventName, props } }));
        // Mirror into TCP internal analytics for /admin/analytics
        _emitInternal(eventName, props);
    } catch (_) {
        /* swallow */
    }
}

export function trackPageView(path, title) {
    if (path === lastPath) return;
    lastPath = path;
    if (window.gtag && GA_ID) window.gtag("event", "page_view", { page_path: path, page_title: title });
    if (window.fbq && META_PIXEL_ID) window.fbq("track", "PageView");
    if (window.ttq && TIKTOK_PIXEL_ID) window.ttq.page();
    track("tcp_page_view", { path, title });
}

// ---- Graph-navigation event helpers ----
// These wrap track() with rabbit-hole inference. When a user moves from
// topic A → entity X → topic B, we tag the second view with `prev_topic` and
// emit a tcp_rabbit_hole_step event. Pure client-state, no backend.

export function trackTopicView(slug, meta = {}) {
    track("tcp_topic_view", { slug, ...meta });
    if (lastTopicSlug && lastTopicSlug !== slug) {
        track("tcp_rabbit_hole_step", {
            kind: "topic_to_topic",
            from: lastTopicSlug,
            to: slug,
            ...meta,
        });
    } else if (lastEntitySlug) {
        track("tcp_rabbit_hole_step", {
            kind: "entity_to_topic",
            from: lastEntitySlug,
            to: slug,
            ...meta,
        });
    }
    lastTopicSlug = slug;
}

export function trackEntityView(slug, meta = {}) {
    track("tcp_entity_view", { slug, ...meta });
    if (lastTopicSlug) {
        track("tcp_rabbit_hole_step", {
            kind: "topic_to_entity",
            from: lastTopicSlug,
            to: slug,
            ...meta,
        });
    }
    lastEntitySlug = slug;
}

export function trackTimelineView() {
    track("tcp_timeline_view");
}

export function trackSourceView(key, meta = {}) {
    track("tcp_source_view", { key, ...meta });
}

export function trackBookmark(action, type, id) {
    track("tcp_bookmark", { action, type, id });
}

export function trackCitationJump(n, key) {
    track("tcp_citation_jump", { n, key });
}

export function trackCitationToVault(n, key) {
    track("tcp_citation_to_vault", { n, key });
}

export function trackSearch(query, resultCount) {
    track("tcp_search", { query, result_count: resultCount });
}

// ---- Build 3 — behavioural intelligence helpers ----

/**
 * Pathway click — fired when a user follows a "Next investigation" exit
 * from one topic to another (Build 4 will wire the UI). Captures the
 * source cluster so editorial can see which pathways actually convert.
 */
export function trackPathwayClick(meta = {}) {
    track("tcp_pathway_click", meta);
}

/**
 * Gated-section view — when a locked section becomes visible to a
 * non-member. Complements tcp_gate_view (which fires for the topic-level
 * gate) by capturing which specific sections drive paywall intent.
 */
export function trackGatedSectionView(meta = {}) {
    track("tcp_gated_section_view", meta);
}

/**
 * Onboarding response — one event per question the user answers.
 * Build 5 will wire the quiz flow; this helper is the contract.
 */
export function trackOnboardingResponse(meta = {}) {
    track("tcp_onboarding_response", meta);
}

/**
 * Install a one-shot scroll-depth tracker for a topic/entity page.
 *
 * Emits scroll_depth_{25,50,75,90} *at most once each*, with the
 * provided meta blob (typically `{ slug, cluster }`). The 4 events
 * combined are enough to compute median reading depth, completion
 * rate and a rough engagement curve per topic.
 *
 * Returns a teardown function — call it on unmount.
 */
export function installScrollDepthTracker(meta = {}) {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return () => {};
    }
    const fired = { 25: false, 50: false, 75: false, 90: false };
    let raf = null;

    const compute = () => {
        raf = null;
        const doc = document.documentElement;
        const body = document.body;
        const scrollTop = window.scrollY || doc.scrollTop || 0;
        const viewport = window.innerHeight || doc.clientHeight || 0;
        const docHeight = Math.max(
            body?.scrollHeight || 0,
            doc.scrollHeight || 0,
            body?.offsetHeight || 0,
            doc.offsetHeight || 0,
        );
        const scrollable = Math.max(docHeight - viewport, 1);
        const pct = ((scrollTop / scrollable) * 100) || 0;
        for (const t of [25, 50, 75, 90]) {
            if (!fired[t] && pct >= t) {
                fired[t] = true;
                track(`tcp_scroll_depth_${t}`, meta);
            }
        }
    };

    const onScroll = () => {
        if (raf != null) return;
        raf = window.requestAnimationFrame(compute);
    };

    // Fire once on install so very short pages get credited if already past 25%.
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
        window.removeEventListener("scroll", onScroll);
        if (raf != null) window.cancelAnimationFrame(raf);
    };
}
